/**
 * Approval Process Storage Router
 *
 * When USE_GOBODO_API is true, delegates process CRUD to Gobodo. When false,
 * uses approvalProcessStorage (localStorage). Request/approval runtime data
 * remains in localStorage for both (Gobodo stores process definitions only).
 */

import type {
  ApprovalProcess,
  ProcessStats,
  CreateApprovalProcessPayload,
  UpdateApprovalProcessPayload
} from '../types/approvalProcesses';
import { USE_GOBODO_API } from '../utils/gobodoUtils';
import { approvalProcessStorage } from './approvalProcessStorage';
import * as gobodoApprovalProcessService from './gobodo/gobodoApprovalProcessService';

export async function getAllProcessesAsync(): Promise<ApprovalProcess[]> {
  if (USE_GOBODO_API) {
    return gobodoApprovalProcessService.getApprovalProcesses();
  }
  return Promise.resolve(approvalProcessStorage.getAllProcesses());
}

export async function getProcessAsync(processId: string): Promise<ApprovalProcess | null> {
  if (USE_GOBODO_API) {
    return gobodoApprovalProcessService.getApprovalProcessById(processId);
  }
  return Promise.resolve(approvalProcessStorage.getProcess(processId));
}

export async function getProcessStatsAsync(processId: string): Promise<ProcessStats | null> {
  const process = USE_GOBODO_API
    ? await gobodoApprovalProcessService.getApprovalProcessById(processId)
    : approvalProcessStorage.getProcess(processId);
  if (!process) return null;
  if (USE_GOBODO_API) {
    return {
      processId: process.id,
      processName: process.name,
      totalSubmitted: 0,
      pending: 0,
      approved: 0,
      rejected: 0,
      recalled: 0,
      approvalRate: 0,
      avgCompletionTime: 0
    };
  }
  return approvalProcessStorage.getProcessStats(processId);
}

export async function createProcessAsync(payload: CreateApprovalProcessPayload): Promise<ApprovalProcess> {
  if (USE_GOBODO_API) {
    return gobodoApprovalProcessService.createApprovalProcess(payload);
  }
  return Promise.resolve(approvalProcessStorage.createProcess(payload));
}

export async function updateProcessAsync(payload: UpdateApprovalProcessPayload): Promise<ApprovalProcess | null> {
  if (USE_GOBODO_API) {
    return gobodoApprovalProcessService.updateApprovalProcess(payload);
  }
  return Promise.resolve(approvalProcessStorage.updateProcess(payload));
}

export async function deleteProcessAsync(processId: string): Promise<boolean> {
  if (USE_GOBODO_API) {
    return gobodoApprovalProcessService.deleteApprovalProcess(processId);
  }
  return Promise.resolve(approvalProcessStorage.deleteProcess(processId));
}

export async function toggleProcessStatusAsync(processId: string): Promise<ApprovalProcess | null> {
  const process = USE_GOBODO_API
    ? await gobodoApprovalProcessService.getApprovalProcessById(processId)
    : approvalProcessStorage.getProcess(processId);
  if (!process) return null;
  if (USE_GOBODO_API) {
    return gobodoApprovalProcessService.updateApprovalProcess({
      id: processId,
      isActive: !process.isActive
    });
  }
  return Promise.resolve(approvalProcessStorage.toggleProcessStatus(processId));
}

export async function cloneProcessAsync(processId: string, newName: string): Promise<ApprovalProcess | null> {
  const original = USE_GOBODO_API
    ? await gobodoApprovalProcessService.getApprovalProcessById(processId)
    : approvalProcessStorage.getProcess(processId);
  if (!original) return null;
  const payload: CreateApprovalProcessPayload = {
    name: newName,
    description: original.description,
    objectType: original.objectType,
    isActive: false,
    order: original.order,
    entryCriteria: original.entryCriteria,
    entryCriteriaLogic: original.entryCriteriaLogic,
    steps: original.steps.map(({ id: _id, ...s }) => s),
    initialSubmissionActions: original.initialSubmissionActions,
    finalApprovalActions: original.finalApprovalActions,
    finalRejectionActions: original.finalRejectionActions,
    recallActions: original.recallActions,
    allowRecall: original.allowRecall,
    recordEditability: original.recordEditability,
    approverCanEdit: original.approverCanEdit,
    approvalEmailTemplateId: original.approvalEmailTemplateId,
    rejectionEmailTemplateId: original.rejectionEmailTemplateId
  };
  if (USE_GOBODO_API) {
    return gobodoApprovalProcessService.createApprovalProcess(payload);
  }
  return Promise.resolve(approvalProcessStorage.cloneProcess(processId, newName));
}
